// courses/{id}/lessons/{id} documents. Moved from aura-courses-admin; every
// change recomputes the lesson's duration and the course totals as before.

import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  writeBatch,
  type DocumentSnapshot,
  type UpdateData,
} from "firebase/firestore";
import { getCoursesFirestore } from "../../config/firebase";
import type { Lesson, LessonFormData } from "../../types/course";
import { COURSES_COLLECTION, LESSONS_COLLECTION, VIDEOS_COLLECTION, updateCourseTotals } from "./course.service";

function lessonRef(courseId: string, lessonId: string) {
  return doc(getCoursesFirestore(), COURSES_COLLECTION, courseId, LESSONS_COLLECTION, lessonId);
}

function lessonsRef(courseId: string) {
  return collection(getCoursesFirestore(), COURSES_COLLECTION, courseId, LESSONS_COLLECTION);
}

function videosRef(courseId: string, lessonId: string) {
  return collection(getCoursesFirestore(), COURSES_COLLECTION, courseId, LESSONS_COLLECTION, lessonId, VIDEOS_COLLECTION);
}

function toLesson(snap: DocumentSnapshot): Lesson {
  const data = snap.data() ?? {};
  return {
    id: snap.id,
    ...data,
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
  } as Lesson;
}

// Sum of the lesson's video durations (minutes); 0 when the read fails.
export async function calculateLessonDuration(courseId: string, lessonId: string): Promise<number> {
  try {
    const videosSnapshot = await getDocs(videosRef(courseId, lessonId));
    let totalDuration = 0;
    videosSnapshot.docs.forEach((videoDoc) => {
      totalDuration += videoDoc.data().duration || 0;
    });
    return totalDuration;
  } catch (error) {
    console.error("Error calculating lesson duration:", error);
    return 0;
  }
}

// Called after every video change; also refreshes the course totals.
export async function updateLessonDuration(courseId: string, lessonId: string): Promise<void> {
  const totalDuration = await calculateLessonDuration(courseId, lessonId);
  await updateDoc(lessonRef(courseId, lessonId), { totalDuration, updatedAt: Timestamp.now() });
  await updateCourseTotals(courseId);
}

export async function getLessonsByCourseId(courseId: string): Promise<Lesson[]> {
  const snapshot = await getDocs(query(lessonsRef(courseId), orderBy("order", "asc")));
  return snapshot.docs.map(toLesson);
}

export async function getLessonById(courseId: string, lessonId: string): Promise<Lesson | null> {
  const snap = await getDoc(lessonRef(courseId, lessonId));
  return snap.exists() ? toLesson(snap) : null;
}

export async function createLesson(courseId: string, lessonData: LessonFormData): Promise<string> {
  const newLesson = {
    ...lessonData,
    totalDuration: 0,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const docRef = await addDoc(lessonsRef(courseId), newLesson);
  await updateCourseTotals(courseId);
  return docRef.id;
}

// Pass `knowledgeCheck: undefined` to remove the knowledge check (Firestore
// rejects undefined, so it becomes a field delete).
export async function updateLesson(courseId: string, lessonId: string, lessonData: Partial<Lesson>): Promise<void> {
  const updateData: UpdateData<Lesson> = { ...lessonData, updatedAt: Timestamp.now() };
  delete updateData.totalDuration;
  delete updateData.createdAt;
  delete updateData.id;
  if ("knowledgeCheck" in lessonData && lessonData.knowledgeCheck === undefined) {
    updateData.knowledgeCheck = deleteField();
  }
  await updateDoc(lessonRef(courseId, lessonId), updateData);
  // The knowledge-check duration counts towards the course total.
  if ("knowledgeCheck" in lessonData) await updateCourseTotals(courseId);
}

// Deletes the lesson with all its videos in one batch.
export async function deleteLesson(courseId: string, lessonId: string): Promise<void> {
  const batch = writeBatch(getCoursesFirestore());
  const videosSnapshot = await getDocs(videosRef(courseId, lessonId));
  videosSnapshot.docs.forEach((videoDoc) => batch.delete(videoDoc.ref));
  batch.delete(lessonRef(courseId, lessonId));
  await batch.commit();
  await updateCourseTotals(courseId);
}

export async function reorderLessons(courseId: string, lessonIds: string[]): Promise<void> {
  const batch = writeBatch(getCoursesFirestore());
  lessonIds.forEach((lessonId, index) => {
    batch.update(lessonRef(courseId, lessonId), { order: index, updatedAt: Timestamp.now() });
  });
  await batch.commit();
}
